/**
 * State-by-state dealer fee data for the pp-no-dealer-fees experiment
 * Source: Legal Research for max fees, CarEdge for averages
 * Last updated: Based on TXCC-7322 requirements
 */

export interface StateDealerFee {
  state: string;
  stateCode: string;
  maxDocFee: number | null; // Legal maximum, null if no cap
  averageDocFee: number; // Average from CarEdge research
}

export const STATE_DEALER_FEES: StateDealerFee[] = [
  { state: 'Alabama', stateCode: 'AL', maxDocFee: null, averageDocFee: 489 },
  { state: 'Alaska', stateCode: 'AK', maxDocFee: null, averageDocFee: 299 },
  { state: 'Arizona', stateCode: 'AZ', maxDocFee: null, averageDocFee: 499 },
  { state: 'Arkansas', stateCode: 'AR', maxDocFee: 129, averageDocFee: 129 },
  { state: 'California', stateCode: 'CA', maxDocFee: 85, averageDocFee: 85 },
  { state: 'Colorado', stateCode: 'CO', maxDocFee: null, averageDocFee: 699 },
  { state: 'Connecticut', stateCode: 'CT', maxDocFee: null, averageDocFee: 599 },
  { state: 'Delaware', stateCode: 'DE', maxDocFee: null, averageDocFee: 475 },
  { state: 'District of Columbia', stateCode: 'DC', maxDocFee: null, averageDocFee: 300 },
  { state: 'Florida', stateCode: 'FL', maxDocFee: null, averageDocFee: 999 },
  { state: 'Georgia', stateCode: 'GA', maxDocFee: null, averageDocFee: 599 },
  { state: 'Hawaii', stateCode: 'HI', maxDocFee: null, averageDocFee: 395 },
  { state: 'Idaho', stateCode: 'ID', maxDocFee: null, averageDocFee: 399 },
  { state: 'Illinois', stateCode: 'IL', maxDocFee: 358, averageDocFee: 347 },
  { state: 'Indiana', stateCode: 'IN', maxDocFee: 200, averageDocFee: 199 },
  { state: 'Iowa', stateCode: 'IA', maxDocFee: 180, averageDocFee: 180 },
  { state: 'Kansas', stateCode: 'KS', maxDocFee: null, averageDocFee: 499 },
  { state: 'Kentucky', stateCode: 'KY', maxDocFee: null, averageDocFee: 450 },
  { state: 'Louisiana', stateCode: 'LA', maxDocFee: 425, averageDocFee: 425 },
  { state: 'Maine', stateCode: 'ME', maxDocFee: null, averageDocFee: 499 },
  { state: 'Maryland', stateCode: 'MD', maxDocFee: 800, averageDocFee: 499 },
  { state: 'Massachusetts', stateCode: 'MA', maxDocFee: null, averageDocFee: 459 },
  { state: 'Michigan', stateCode: 'MI', maxDocFee: 280, averageDocFee: 260 },
  { state: 'Minnesota', stateCode: 'MN', maxDocFee: 350, averageDocFee: 125 },
  { state: 'Mississippi', stateCode: 'MS', maxDocFee: 425, averageDocFee: 425 },
  { state: 'Missouri', stateCode: 'MO', maxDocFee: 604, averageDocFee: 565 },
  { state: 'Montana', stateCode: 'MT', maxDocFee: null, averageDocFee: 299 },
  { state: 'Nebraska', stateCode: 'NE', maxDocFee: null, averageDocFee: 299 },
  { state: 'Nevada', stateCode: 'NV', maxDocFee: null, averageDocFee: 499 },
  { state: 'New Hampshire', stateCode: 'NH', maxDocFee: null, averageDocFee: 375 },
  { state: 'New Jersey', stateCode: 'NJ', maxDocFee: null, averageDocFee: 695 },
  { state: 'New Mexico', stateCode: 'NM', maxDocFee: null, averageDocFee: 339 },
  { state: 'New York', stateCode: 'NY', maxDocFee: 175, averageDocFee: 175 },
  { state: 'North Carolina', stateCode: 'NC', maxDocFee: null, averageDocFee: 699 },
  { state: 'North Dakota', stateCode: 'ND', maxDocFee: null, averageDocFee: 299 },
  { state: 'Ohio', stateCode: 'OH', maxDocFee: 387, averageDocFee: 250 },
  { state: 'Oklahoma', stateCode: 'OK', maxDocFee: null, averageDocFee: 599 },
  { state: 'Oregon', stateCode: 'OR', maxDocFee: 250, averageDocFee: 250 },
  { state: 'Pennsylvania', stateCode: 'PA', maxDocFee: 477, averageDocFee: 449 },
  { state: 'Rhode Island', stateCode: 'RI', maxDocFee: 400, averageDocFee: 399 },
  { state: 'South Carolina', stateCode: 'SC', maxDocFee: null, averageDocFee: 400 },
  { state: 'South Dakota', stateCode: 'SD', maxDocFee: null, averageDocFee: 200 },
  { state: 'Tennessee', stateCode: 'TN', maxDocFee: null, averageDocFee: 499 },
  { state: 'Texas', stateCode: 'TX', maxDocFee: 225, averageDocFee: 150 },
  { state: 'Utah', stateCode: 'UT', maxDocFee: null, averageDocFee: 299 },
  { state: 'Vermont', stateCode: 'VT', maxDocFee: null, averageDocFee: 200 },
  { state: 'Virginia', stateCode: 'VA', maxDocFee: null, averageDocFee: 799 },
  { state: 'Washington', stateCode: 'WA', maxDocFee: 200, averageDocFee: 199 },
  { state: 'West Virginia', stateCode: 'WV', maxDocFee: 575, averageDocFee: 250 },
  { state: 'Wisconsin', stateCode: 'WI', maxDocFee: null, averageDocFee: 299 },
  { state: 'Wyoming', stateCode: 'WY', maxDocFee: null, averageDocFee: 500 }
];

/**
 * Get dealer fee data for a specific state
 * Supports both state code (e.g., 'CA', 'TX') and full name (e.g., 'California', 'Texas')
 * @param stateIdentifier - Two-letter state code or full state name
 * @returns StateDealerFee object or null if state not found
 */
export const getDealerFeeData = (stateIdentifier: string): StateDealerFee | null => {
  const normalized = stateIdentifier.toLowerCase();
  return (
    STATE_DEALER_FEES.find(
      state =>
        state.stateCode.toLowerCase() === normalized || state.state.toLowerCase() === normalized
    ) ?? null
  );
};

/**
 * Get the dealer fee amount to display for a state
 * Always uses max fee if available (states with legal caps), otherwise uses average
 * @param stateData - StateDealerFee object for the user's state
 * @returns The fee amount to display
 */
export const getDealerFeeAmount = (stateData: StateDealerFee): number => {
  return stateData.maxDocFee ?? stateData.averageDocFee;
};

/**
 * Get the type of fee being displayed (maximum or average)
 * @param stateData - StateDealerFee object for the user's state
 * @returns 'maximum' if max fee exists, 'average' otherwise
 */
export const getDealerFeeType = (stateData: StateDealerFee): 'maximum' | 'average' => {
  return stateData.maxDocFee !== null ? 'maximum' : 'average';
};
